import { useEffect, useState } from 'react';
import { ClientDatabase, IClient } from '~/types/client';
import useData from '../data/useData';
import useSyncData from '../data/useSyncData';

export const useClient = (clientID: string) => {
    const client = useData<IClient>(() => ClientDatabase.getClient(clientID));
    return client;
};

export const useSyncClient = (clientID: string) => {
    // Guard empty/missing id: `doc(db, 'clients', '')` throws synchronously at
    // render (invalid Firestore reference). Skip the subscription and resolve to
    // no client instead of crashing (e.g. /account opened without navigation state).
    const subscribe = clientID ? ClientDatabase.subscribe(clientID) : (_onValueChange: () => void) => () => {};
    const getSnapshot = () =>
        clientID ? ClientDatabase.getClient(clientID) : Promise.resolve(undefined as unknown as IClient);

    const client = useSyncData<IClient>(subscribe, getSnapshot, [clientID]);
    return client;
};

export const useClients = (clientIDs: string[]) => {
    const clients = useData<IClient[]>(() => ClientDatabase.getClients(clientIDs));
    return clients;
};

/**
 * Live version of useClients: subscribes to each client document so the list
 * reflects real-time changes (a screen coming online, switching game, gaining
 * players). Used by the animator establishments list, whose LIVE/jeu/joueurs
 * state must stay fresh without a manual reload. Order follows `clientIDs`.
 *
 * Returns undefined until the first snapshot arrives, then a list that fills in
 * progressively as each client's snapshot lands (not all-or-nothing) — a slow
 * client never blocks the others. Empty/falsy ids are dropped to avoid
 * `doc(db, 'clients', '')` throwing (same guard as useSyncClient).
 */
export const useSyncClients = (clientIDs: string[]) => {
    const [clients, setClients] = useState<IClient[]>();
    const ids = clientIDs.filter(Boolean);
    // Re-subscribe only when the SET of ids changes, not on array identity.
    const key = ids.join(',');

    useEffect(() => {
        if (ids.length === 0) {
            setClients([]);
            return undefined;
        }
        const byID = new Map<string, IClient>();
        const emit = () => setClients(ids.map((id) => byID.get(id)).filter((c): c is IClient => !!c));
        const unsubs = ids.map((id) =>
            ClientDatabase.subscribeClient(id, (client) => {
                if (client) byID.set(id, client);
                else byID.delete(id);
                emit();
            })
        );
        return () => unsubs.forEach((unsub) => unsub());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    return clients;
};
